import { useMemo } from 'react';
import { encode } from 'uqr';

import type { Language } from '@/i18n/dictionary';

import { uscisDocumentsUrl } from '../uscisDocuments';

export interface UscisDocumentsQrProps {
  /**
   * The language the kiosk is showing. ⛔ A language, never a URL: the only strings that can reach the
   * encoder are the two constants in `uscisDocuments.ts` [decision 0370 narrowing 0114].
   */
  readonly language: Language;
  /** The id of the visible caption that names the code for a screen reader. */
  readonly labelledBy: string;
}

/**
 * The 112px code on the I-9 documents overlay [Figma · component `QR` 11906:24457, its 112 × 112
 * `Rectangle` placeholder, read live 2026-10-01].
 *
 * ⭐ **Drawn here, from a constant, with no request to any origin** [KAP-6; 0370]. `uqr` is a dependency-free
 * encoder bundled into the app: it turns the string into a module matrix in memory, and this component draws
 * that matrix as one SVG path. No image is fetched, no QR service is called, and no `innerHTML` is written —
 * the CSP needs no new origin, and the code works on a kiosk that has lost its network.
 *
 * - **Error correction `M`** (15%): a code on a lit screen meets glare more than damage. Both URLs land on
 *   version 5 at this level — 37 modules, ≈3px each at 112px — so the two languages draw at the same
 *   density.
 * - **No quiet zone inside the SVG.** The scan block around it IS the quiet zone, and it is not even: at
 *   ≈3px a module, the 24px above the code (22 of `--scan-surface` plus the 2px light border) is ≈8
 *   modules, and the sides are far wider (the block is 327 wide, ≈35 modules each side). BELOW the code
 *   the caption sits 8px under it, as the frame draws it — ≈2.6 modules to the caption's line box and 3.96
 *   to its first glyph (12px, measured in the 1440 screenshot), so the bottom carries about the 4 modules
 *   the standard asks for and no more. A white margin inside the 112px would only shrink the modules. The
 *   six real-browser screenshots in the KAP-6 evidence decode with ZXing; a phone at arm's length on the
 *   kiosk hardware is the check still owed.
 * - **Dark modules on a light surface in both themes**: the component documents these colours as raw
 *   exceptions, because an inverted code does not scan on every phone.
 */
export function UscisDocumentsQr({ language, labelledBy }: UscisDocumentsQrProps) {
  const { size, path } = useMemo(() => {
    const qr = encode(uscisDocumentsUrl(language), { ecc: 'M', border: 0 });
    return { size: qr.size, path: modulePath(qr.data) };
  }, [language]);

  return (
    <svg
      aria-labelledby={labelledBy}
      className="block h-28 w-28"
      role="img"
      shapeRendering="crispEdges"
      viewBox={`0 0 ${size} ${size}`}
    >
      <path d={path} fill="var(--scan-module)" />
    </svg>
  );
}

/**
 * The matrix as ONE path: each horizontal run of dark modules becomes `M<x> <y>h<n>v1h-<n>z`.
 *
 * One node rather than a `<rect>` per module — a version-5 code is ~700 dark modules, and this kiosk boots
 * on cheap hardware. Runs rather than single modules keep the path short and leave no hairline seams between
 * neighbours when the 37-module grid is scaled to a non-integer pixel size.
 */
function modulePath(rows: readonly (readonly boolean[])[]): string {
  const parts: string[] = [];
  rows.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      if (row[x] !== true) {
        x += 1;
        continue;
      }
      const start = x;
      while (row[x] === true) x += 1;
      const run = x - start;
      parts.push(`M${start} ${y}h${run}v1h-${run}z`);
    }
  });
  return parts.join('');
}
